import {
    ServerCIEnvironment,
    endPointToServerMapping
} from '~/servers/servers';
import type { LaunchDarklyConfig } from './types';

/**
 * LaunchDarkly client-side IDs by environment
 *
 * Client-side IDs are public values safe to commit to version control.
 * They only allow flag evaluation for the associated environment —
 * they cannot modify flag state or access sensitive data.
 *
 * Obtain these from the LaunchDarkly dashboard:
 *   Account Settings → Projects → <project> → Environments → Client-side ID
 */
const stageConfig: LaunchDarklyConfig = {
    appName: ServerCIEnvironment.STAGE,
    clientSideId: '6a70eb091005aa0ab511f124'
};

const uatConfig: LaunchDarklyConfig = {
    appName: ServerCIEnvironment.UAT,
    clientSideId: '6a2c4914e881db0ae5e75390'
};

const cnUatConfig: LaunchDarklyConfig = {
    appName: ServerCIEnvironment.CN_UAT,
    clientSideId: '6a2c4914e881db0ae5e75390' // same as uat
};

const demoConfig: LaunchDarklyConfig = {
    appName: ServerCIEnvironment.DEMO,
    clientSideId: '6a2c4960e13fe80a81729ea7'
};

const developmentConfig: LaunchDarklyConfig = {
    appName: ServerCIEnvironment.DEVELOPMENT,
    clientSideId: '6a1d8ddb4593c00a985c3280'
};

const sandboxConfig: LaunchDarklyConfig = {
    appName: ServerCIEnvironment.SANDBOX,
    clientSideId: '6a1d8ddb4593c00a985c3280' // same as development
};

const productionConfig: LaunchDarklyConfig = {
    appName: ServerCIEnvironment.PRODUCTION,
    clientSideId: '6a1d8ddb4593c00a985c3281'
};

/**
 * A mapping of CI environments to their LaunchDarkly configuration
 */
export const launchDarklyConfigMap: Record<string, LaunchDarklyConfig> = {
    [ServerCIEnvironment.STAGE]: stageConfig,
    [ServerCIEnvironment.DEMO]: demoConfig,
    [ServerCIEnvironment.DEVELOPMENT]: developmentConfig,
    [ServerCIEnvironment.SANDBOX]: sandboxConfig,
    [ServerCIEnvironment.UAT]: uatConfig,
    [ServerCIEnvironment.CN_UAT]: cnUatConfig,
    [ServerCIEnvironment.PRODUCTION]: productionConfig
};

/**
 * Retrieve the LaunchDarkly client configuration for the target `hostname`
 *
 * When a client configuration does not exist for a hostname,
 * the returned configuration will default to the `stage` configuration
 */
export const getLaunchDarklyConfig = (hostName: string): LaunchDarklyConfig => {
    const currentCIEnv =
        endPointToServerMapping[
            hostName as keyof typeof endPointToServerMapping
        ]?.CIEnv;

    return (
        launchDarklyConfigMap[currentCIEnv] ||
        launchDarklyConfigMap[ServerCIEnvironment.STAGE]
    );
};
